import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { EmptyState } from '@/components/shared/EmptyState';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Shield, PlugZap, RefreshCw, PlayCircle, FlaskConical, Trash2, Pencil, Plug } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyConnectionForm = {
  id: '',
  org_id: '',
  name: '',
  source_type: 'gpm_rest',
  poll_interval_seconds: 60,
  status: 'active',
  feature_flag_use_live_data: false,
  config_json: '{}',
  gpm_base_url: '',
  gpm_username: '',
  gpm_password: '',
  sftp_host: '',
  sftp_port: 22,
  sftp_username: '',
  sftp_password: '',
  sftp_remote_path: '.',
  sftp_file_glob: '*',
  sftp_file_format: 'csv',
};

const emptyMappingForm = {
  id: '',
  connection_id: '',
  mapping_name: '',
  mapping_type: 'telemetry',
  canonical_key: '',
  target_unit: '',
  selector_metric_key: '',
  selector_datasource_name: '',
  selector_datasource_id: '',
  selector_source_entity_id: '',
  transform_multiply_by: '',
  transform_divide_by: '',
  transform_offset: '',
  transform_round: '',
  is_active: true,
};

const emptyLinkForm = {
  id: '',
  connection_id: '',
  asset_id: '',
  external_plant_id: '',
  external_plant_name: '',
  link_status: 'active',
  metadata_json: '{}',
};

function parseJsonSafe(value, fallback = {}) {
  try {
    if (!value || !String(value).trim()) return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

export default function DataIntegrationsPage() {
  const { user, org, hasRole } = useAuth();
  const isPlatformAdmin = hasRole(['superadmin', 'platform_admin']);
  const canManage = hasRole(['superadmin', 'platform_admin', 'org_admin']);

  const [organizations, setOrganizations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [connections, setConnections] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [links, setLinks] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [quarantine, setQuarantine] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState('');

  const [connectionForm, setConnectionForm] = useState(emptyConnectionForm);
  const [mappingForm, setMappingForm] = useState(emptyMappingForm);
  const [linkForm, setLinkForm] = useState(emptyLinkForm);

  const orgScopedConnections = useMemo(() => {
    if (selectedOrg === 'all') return connections;
    return connections.filter((item) => item.org_id === selectedOrg);
  }, [connections, selectedOrg]);

  const orgScopedAssets = useMemo(() => {
    if (selectedOrg === 'all') return assets;
    return assets.filter((item) => item.org_id === selectedOrg);
  }, [assets, selectedOrg]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const orgQuery = selectedOrg !== 'all' ? `?org_id=${selectedOrg}` : '';
      const [orgRes, assetsRes, connectionsRes, mappingsRes, linksRes, jobsRes, quarantineRes] = await Promise.all([
        isPlatformAdmin ? axios.get(`${API}/organizations`) : Promise.resolve({ data: org ? [org] : [] }),
        axios.get(`${API}/assets`),
        axios.get(`${API}/integrations${orgQuery}`),
        axios.get(`${API}/integration-mappings${orgQuery}`),
        axios.get(`${API}/integration-asset-links${orgQuery}`),
        axios.get(`${API}/integration-jobs${orgQuery}`),
        axios.get(`${API}/integration-quarantine${orgQuery}`),
      ]);
      setOrganizations(orgRes.data || []);
      setAssets(assetsRes.data || []);
      setConnections(connectionsRes.data || []);
      setMappings(mappingsRes.data || []);
      setLinks(linksRes.data || []);
      setJobs(jobsRes.data || []);
      setQuarantine(quarantineRes.data || []);
    } catch (error) {
      console.error('Failed to load integration data:', error);
      toast.error(error.response?.data?.detail || 'Failed to load integration data');
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin, org, selectedOrg]);

  useEffect(() => {
    if (!isPlatformAdmin && org?.id && selectedOrg === 'all') {
      setSelectedOrg(org.id);
    }
  }, [isPlatformAdmin, org, selectedOrg]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const resetConnectionForm = () => {
    setConnectionForm({
      ...emptyConnectionForm,
      org_id: selectedOrg !== 'all' ? selectedOrg : org?.id || '',
    });
  };

  const resetMappingForm = () => setMappingForm(emptyMappingForm);
  const resetLinkForm = () => setLinkForm(emptyLinkForm);

  useEffect(() => {
    if (!connectionForm.org_id) {
      resetConnectionForm();
    }
  }, [selectedOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildCredentialsFromForm = () => {
    if (connectionForm.source_type === 'gpm_rest') {
      const credentials = {
        base_url: connectionForm.gpm_base_url.trim(),
        username: connectionForm.gpm_username.trim(),
        password: connectionForm.gpm_password,
      };
      if (!credentials.base_url || !credentials.username) return null;
      if (!credentials.password && !connectionForm.id) return null;
      return credentials;
    }
    const credentials = {
      host: connectionForm.sftp_host.trim(),
      port: Number(connectionForm.sftp_port || 22),
      username: connectionForm.sftp_username.trim(),
      password: connectionForm.sftp_password,
      remote_path: connectionForm.sftp_remote_path.trim() || '.',
    };
    if (!credentials.host || !credentials.username) return null;
    if (!credentials.password && !connectionForm.id) return null;
    return credentials;
  };

  const saveConnection = async () => {
    if (!connectionForm.name.trim()) {
      toast.error('Connection name is required');
      return;
    }
    const targetOrg = isPlatformAdmin
      ? connectionForm.org_id
      : org?.id;
    if (!targetOrg) {
      toast.error('Organization is required');
      return;
    }

    const credentials = buildCredentialsFromForm();
    if (!credentials && !connectionForm.id) {
      toast.error('Please provide required credential fields');
      return;
    }

    const config = parseJsonSafe(connectionForm.config_json, {});
    if (connectionForm.source_type === 'sftp_files') {
      config.file_glob = connectionForm.sftp_file_glob || '*';
      config.file_format = connectionForm.sftp_file_format || 'csv';
      config.remote_path = connectionForm.sftp_remote_path || '.';
    }

    const payload = {
      org_id: targetOrg,
      name: connectionForm.name.trim(),
      source_type: connectionForm.source_type,
      poll_interval_seconds: Number(connectionForm.poll_interval_seconds || 60),
      status: connectionForm.status,
      feature_flag_use_live_data: !!connectionForm.feature_flag_use_live_data,
      config,
    };
    if (credentials) {
      payload.credentials = credentials;
    }

    try {
      setBusyAction('save-connection');
      if (connectionForm.id) {
        await axios.patch(`${API}/integrations/${connectionForm.id}`, payload);
      } else {
        await axios.post(`${API}/integrations`, payload);
      }
      resetConnectionForm();
      await fetchAll();
    } catch (error) {
      console.error('Failed to save integration connection:', error);
      toast.error(error.response?.data?.detail || 'Failed to save integration connection');
    } finally {
      setBusyAction('');
    }
  };

  const editConnection = (record) => {
    const config = record.config || {};
    setConnectionForm({
      ...emptyConnectionForm,
      id: record.id,
      org_id: record.org_id || '',
      name: record.name || '',
      source_type: record.source_type || 'gpm_rest',
      poll_interval_seconds: record.poll_interval_seconds || 60,
      status: record.status || 'active',
      feature_flag_use_live_data: !!record.feature_flag_use_live_data,
      config_json: JSON.stringify(config, null, 2),
      gpm_base_url: record.credentials_hint?.base_url || '',
      gpm_username: record.credentials_hint?.username || '',
      sftp_host: record.credentials_hint?.host || '',
      sftp_port: record.credentials_hint?.port || 22,
      sftp_username: record.credentials_hint?.username || '',
      sftp_remote_path: record.credentials_hint?.remote_path || config.remote_path || '.',
      sftp_file_glob: config.file_glob || '*',
      sftp_file_format: config.file_format || 'csv',
    });
  };

  const deleteConnection = async (record) => {
    if (!window.confirm(`Delete integration "${record.name}"?`)) return;
    try {
      setBusyAction(`delete-connection-${record.id}`);
      await axios.delete(`${API}/integrations/${record.id}`);
      if (connectionForm.id === record.id) resetConnectionForm();
      await fetchAll();
    } catch (error) {
      console.error('Failed to delete connection:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete connection');
    } finally {
      setBusyAction('');
    }
  };

  const testConnection = async (record) => {
    try {
      setBusyAction(`test-${record.id}`);
      const response = await axios.post(`${API}/integrations/${record.id}/test`);
      const count = (response.data?.plant_candidates || []).length;
      toast.success(`Connection successful. Plant candidates discovered: ${count}`);
      await fetchAll();
    } catch (error) {
      console.error('Failed to test connection:', error);
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setBusyAction('');
    }
  };

  const syncConnection = async (record) => {
    try {
      setBusyAction(`sync-${record.id}`);
      const response = await axios.post(`${API}/integrations/${record.id}/sync`);
      toast.success(`Sync ${response.data?.status || 'completed'}: processed ${response.data?.records_processed || 0}, failed ${response.data?.records_failed || 0}`);
      await fetchAll();
    } catch (error) {
      console.error('Failed to sync connection:', error);
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setBusyAction('');
    }
  };

  const saveMapping = async () => {
    if (!mappingForm.connection_id || !mappingForm.canonical_key.trim()) {
      toast.error('Connection and canonical key are required');
      return;
    }
    const sourceSelector = {};
    if (mappingForm.selector_metric_key.trim()) sourceSelector.metric_key = mappingForm.selector_metric_key.trim();
    if (mappingForm.selector_datasource_name.trim()) sourceSelector.datasource_name = mappingForm.selector_datasource_name.trim();
    if (mappingForm.selector_datasource_id.trim()) sourceSelector.datasource_id = mappingForm.selector_datasource_id.trim();
    if (mappingForm.selector_source_entity_id.trim()) sourceSelector.source_entity_id = mappingForm.selector_source_entity_id.trim();

    const transformRules = {};
    if (mappingForm.transform_multiply_by !== '') transformRules.multiply_by = Number(mappingForm.transform_multiply_by);
    if (mappingForm.transform_divide_by !== '') transformRules.divide_by = Number(mappingForm.transform_divide_by);
    if (mappingForm.transform_offset !== '') transformRules.offset = Number(mappingForm.transform_offset);
    if (mappingForm.transform_round !== '') transformRules.round = Number(mappingForm.transform_round);

    const payload = {
      connection_id: mappingForm.connection_id,
      mapping_name: mappingForm.mapping_name.trim() || mappingForm.canonical_key.trim(),
      mapping_type: mappingForm.mapping_type,
      canonical_key: mappingForm.canonical_key.trim(),
      target_unit: mappingForm.target_unit.trim(),
      source_selector: sourceSelector,
      transform_rules: transformRules,
      is_active: !!mappingForm.is_active,
    };

    try {
      setBusyAction('save-mapping');
      if (mappingForm.id) {
        await axios.patch(`${API}/integration-mappings/${mappingForm.id}`, payload);
      } else {
        await axios.post(`${API}/integration-mappings`, payload);
      }
      resetMappingForm();
      await fetchAll();
    } catch (error) {
      console.error('Failed to save mapping:', error);
      toast.error(error.response?.data?.detail || 'Failed to save mapping');
    } finally {
      setBusyAction('');
    }
  };

  const editMapping = (record) => {
    const selector = record.source_selector || {};
    const rules = record.transform_rules || {};
    setMappingForm({
      ...emptyMappingForm,
      id: record.id,
      connection_id: record.connection_id || '',
      mapping_name: record.mapping_name || '',
      mapping_type: record.mapping_type || 'telemetry',
      canonical_key: record.canonical_key || '',
      target_unit: record.target_unit || '',
      selector_metric_key: selector.metric_key || '',
      selector_datasource_name: selector.datasource_name || '',
      selector_datasource_id: selector.datasource_id || '',
      selector_source_entity_id: selector.source_entity_id || '',
      transform_multiply_by: rules.multiply_by ?? '',
      transform_divide_by: rules.divide_by ?? '',
      transform_offset: rules.offset ?? '',
      transform_round: rules.round ?? '',
      is_active: !!record.is_active,
    });
  };

  const deleteMapping = async (record) => {
    if (!window.confirm(`Delete mapping "${record.mapping_name}"?`)) return;
    try {
      setBusyAction(`delete-mapping-${record.id}`);
      await axios.delete(`${API}/integration-mappings/${record.id}`);
      if (mappingForm.id === record.id) resetMappingForm();
      await fetchAll();
    } catch (error) {
      console.error('Failed to delete mapping:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete mapping');
    } finally {
      setBusyAction('');
    }
  };

  const saveLink = async () => {
    if (!linkForm.connection_id || !linkForm.asset_id || !linkForm.external_plant_id.trim()) {
      toast.error('Connection, asset, and external plant ID are required');
      return;
    }
    const payload = {
      connection_id: linkForm.connection_id,
      asset_id: linkForm.asset_id,
      external_plant_id: linkForm.external_plant_id.trim(),
      external_plant_name: linkForm.external_plant_name.trim(),
      link_status: linkForm.link_status,
      metadata: parseJsonSafe(linkForm.metadata_json, {}),
    };
    try {
      setBusyAction('save-link');
      if (linkForm.id) {
        await axios.patch(`${API}/integration-asset-links/${linkForm.id}`, payload);
      } else {
        await axios.post(`${API}/integration-asset-links`, payload);
      }
      resetLinkForm();
      await fetchAll();
    } catch (error) {
      console.error('Failed to save link:', error);
      toast.error(error.response?.data?.detail || 'Failed to save link');
    } finally {
      setBusyAction('');
    }
  };

  const editLink = (record) => {
    setLinkForm({
      ...emptyLinkForm,
      id: record.id,
      connection_id: record.connection_id || '',
      asset_id: record.asset_id || '',
      external_plant_id: record.external_plant_id || '',
      external_plant_name: record.external_plant_name || '',
      link_status: record.link_status || 'active',
      metadata_json: JSON.stringify(record.metadata || {}, null, 2),
    });
  };

  const deleteLink = async (record) => {
    if (!window.confirm(`Delete link for plant "${record.external_plant_id}"?`)) return;
    try {
      setBusyAction(`delete-link-${record.id}`);
      await axios.delete(`${API}/integration-asset-links/${record.id}`);
      if (linkForm.id === record.id) resetLinkForm();
      await fetchAll();
    } catch (error) {
      console.error('Failed to delete link:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete link');
    } finally {
      setBusyAction('');
    }
  };

  const replayQuarantine = async (record) => {
    try {
      setBusyAction(`replay-${record.id}`);
      await axios.post(`${API}/integration-quarantine/${record.id}/replay`);
      await fetchAll();
    } catch (error) {
      console.error('Failed to replay quarantine record:', error);
      toast.error(error.response?.data?.detail || 'Replay failed');
    } finally {
      setBusyAction('');
    }
  };

  if (!canManage) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-500">Data integrations can be managed by Admin and Org Admin roles.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Data Integrations</h1>
            <p className="text-slate-500">Manage source connections, field mappings, asset links, sync jobs, and quarantine replay.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchAll}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Connections</p><p className="text-2xl font-bold">{connections.length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Mappings</p><p className="text-2xl font-bold">{mappings.length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Asset Links</p><p className="text-2xl font-bold">{links.length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Jobs</p><p className="text-2xl font-bold">{jobs.length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Quarantine</p><p className="text-2xl font-bold">{quarantine.length}</p></CardContent></Card>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-[320px]">
                <Label>Organization Scope</Label>
                <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isPlatformAdmin && <SelectItem value="all">All Organizations</SelectItem>}
                    {(organizations || []).map((item) => (
                      <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="connections" className="space-y-4">
          <TabsList>
            <TabsTrigger value="connections">Connections</TabsTrigger>
            <TabsTrigger value="mappings">Mappings</TabsTrigger>
            <TabsTrigger value="links">Asset Links</TabsTrigger>
            <TabsTrigger value="jobs">Jobs</TabsTrigger>
            <TabsTrigger value="quarantine">Quarantine</TabsTrigger>
          </TabsList>

          <TabsContent value="connections" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{connectionForm.id ? 'Edit Connection' : 'Create Connection'}</CardTitle>
                <CardDescription>Configure GPM REST or SFTP source, poll interval, and live-data feature flag.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  {isPlatformAdmin && (
                    <div>
                      <Label>Organization</Label>
                      <Select value={connectionForm.org_id || ''} onValueChange={(value) => setConnectionForm((state) => ({ ...state, org_id: value }))}>
                        <SelectTrigger><SelectValue placeholder="Select org" /></SelectTrigger>
                        <SelectContent>
                          {(organizations || []).map((item) => (
                            <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>Name</Label>
                    <Input value={connectionForm.name} onChange={(e) => setConnectionForm((state) => ({ ...state, name: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Source Type</Label>
                    <Select value={connectionForm.source_type} onValueChange={(value) => setConnectionForm((state) => ({ ...state, source_type: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gpm_rest">GPM REST</SelectItem>
                        <SelectItem value="sftp_files">SFTP Files</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Poll Interval (seconds)</Label>
                    <Input type="number" min={30} max={3600} value={connectionForm.poll_interval_seconds} onChange={(e) => setConnectionForm((state) => ({ ...state, poll_interval_seconds: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={connectionForm.status} onValueChange={(value) => setConnectionForm((state) => ({ ...state, status: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!connectionForm.feature_flag_use_live_data}
                        onChange={(e) => setConnectionForm((state) => ({ ...state, feature_flag_use_live_data: e.target.checked }))}
                      />
                      Use canonical live data for assets
                    </label>
                  </div>
                </div>

                {connectionForm.source_type === 'gpm_rest' ? (
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Base URL</Label>
                      <Input placeholder="https://your-gpm-host" value={connectionForm.gpm_base_url} onChange={(e) => setConnectionForm((state) => ({ ...state, gpm_base_url: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Username</Label>
                      <Input value={connectionForm.gpm_username} onChange={(e) => setConnectionForm((state) => ({ ...state, gpm_username: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Password {connectionForm.id ? '(leave blank to keep)' : ''}</Label>
                      <Input type="password" value={connectionForm.gpm_password} onChange={(e) => setConnectionForm((state) => ({ ...state, gpm_password: e.target.value }))} />
                    </div>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-4 gap-4">
                    <div>
                      <Label>Host</Label>
                      <Input value={connectionForm.sftp_host} onChange={(e) => setConnectionForm((state) => ({ ...state, sftp_host: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Port</Label>
                      <Input type="number" value={connectionForm.sftp_port} onChange={(e) => setConnectionForm((state) => ({ ...state, sftp_port: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Username</Label>
                      <Input value={connectionForm.sftp_username} onChange={(e) => setConnectionForm((state) => ({ ...state, sftp_username: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Password {connectionForm.id ? '(leave blank to keep)' : ''}</Label>
                      <Input type="password" value={connectionForm.sftp_password} onChange={(e) => setConnectionForm((state) => ({ ...state, sftp_password: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Remote Path</Label>
                      <Input value={connectionForm.sftp_remote_path} onChange={(e) => setConnectionForm((state) => ({ ...state, sftp_remote_path: e.target.value }))} />
                    </div>
                    <div>
                      <Label>File Glob</Label>
                      <Input value={connectionForm.sftp_file_glob} onChange={(e) => setConnectionForm((state) => ({ ...state, sftp_file_glob: e.target.value }))} />
                    </div>
                    <div>
                      <Label>File Format</Label>
                      <Select value={connectionForm.sftp_file_format} onValueChange={(value) => setConnectionForm((state) => ({ ...state, sftp_file_format: value }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="csv">CSV</SelectItem>
                          <SelectItem value="json">JSON</SelectItem>
                          <SelectItem value="parquet">Parquet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div>
                  <Label>Additional Config (JSON)</Label>
                  <Textarea rows={5} value={connectionForm.config_json} onChange={(e) => setConnectionForm((state) => ({ ...state, config_json: e.target.value }))} />
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button onClick={saveConnection} disabled={busyAction === 'save-connection'}>
                    <PlugZap className="w-4 h-4 mr-2" />
                    {connectionForm.id ? 'Update Connection' : 'Create Connection'}
                  </Button>
                  <Button variant="outline" onClick={resetConnectionForm}>Clear</Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Connections</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {loading ? <p className="text-slate-500">Loading...</p> : orgScopedConnections.length === 0 ? (
                  <EmptyState
                    icon={Plug}
                    title="No integrations"
                    description="Connect a data source to start ingesting telemetry."
                  />
                ) : orgScopedConnections.map((item) => (
                  <div key={item.id} className="border rounded-lg p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{item.name}</p>
                      <p className="text-sm text-slate-500">{item.source_type} • Poll {item.poll_interval_seconds}s • Org {item.org_id}</p>
                      <div className="mt-2 flex gap-2 flex-wrap">
                        <Badge variant="outline">{item.status}</Badge>
                        {item.feature_flag_use_live_data && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Live Data On</Badge>}
                        {item.last_sync_at && <Badge variant="outline">Last Sync {new Date(item.last_sync_at).toLocaleString()}</Badge>}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => editConnection(item)}><Pencil className="w-4 h-4 mr-1" />Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => testConnection(item)} disabled={busyAction === `test-${item.id}`}><FlaskConical className="w-4 h-4 mr-1" />Test</Button>
                      <Button size="sm" onClick={() => syncConnection(item)} disabled={busyAction === `sync-${item.id}`}><PlayCircle className="w-4 h-4 mr-1" />Sync</Button>
                      <Button size="sm" variant="destructive" onClick={() => deleteConnection(item)} disabled={busyAction === `delete-connection-${item.id}`}><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="mappings" className="space-y-4">
            <Card>
              <CardHeader><CardTitle>{mappingForm.id ? 'Edit Mapping' : 'Create Mapping'}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Connection</Label>
                    <Select value={mappingForm.connection_id} onValueChange={(value) => setMappingForm((state) => ({ ...state, connection_id: value }))}>
                      <SelectTrigger><SelectValue placeholder="Select connection" /></SelectTrigger>
                      <SelectContent>
                        {orgScopedConnections.map((item) => (
                          <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Mapping Name</Label>
                    <Input value={mappingForm.mapping_name} onChange={(e) => setMappingForm((state) => ({ ...state, mapping_name: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={mappingForm.mapping_type} onValueChange={(value) => setMappingForm((state) => ({ ...state, mapping_type: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="telemetry">Telemetry</SelectItem>
                        <SelectItem value="event">Event</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Canonical Key</Label>
                    <Input value={mappingForm.canonical_key} onChange={(e) => setMappingForm((state) => ({ ...state, canonical_key: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Target Unit</Label>
                    <Input value={mappingForm.target_unit} onChange={(e) => setMappingForm((state) => ({ ...state, target_unit: e.target.value }))} />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!mappingForm.is_active} onChange={(e) => setMappingForm((state) => ({ ...state, is_active: e.target.checked }))} />
                      Active Mapping
                    </label>
                  </div>
                </div>
                <div className="grid md:grid-cols-4 gap-4">
                  <div><Label>Selector Metric Key</Label><Input value={mappingForm.selector_metric_key} onChange={(e) => setMappingForm((state) => ({ ...state, selector_metric_key: e.target.value }))} /></div>
                  <div><Label>Selector Datasource Name</Label><Input value={mappingForm.selector_datasource_name} onChange={(e) => setMappingForm((state) => ({ ...state, selector_datasource_name: e.target.value }))} /></div>
                  <div><Label>Selector Datasource ID</Label><Input value={mappingForm.selector_datasource_id} onChange={(e) => setMappingForm((state) => ({ ...state, selector_datasource_id: e.target.value }))} /></div>
                  <div><Label>Selector Source Entity ID</Label><Input value={mappingForm.selector_source_entity_id} onChange={(e) => setMappingForm((state) => ({ ...state, selector_source_entity_id: e.target.value }))} /></div>
                </div>
                <div className="grid md:grid-cols-4 gap-4">
                  <div><Label>Multiply By</Label><Input value={mappingForm.transform_multiply_by} onChange={(e) => setMappingForm((state) => ({ ...state, transform_multiply_by: e.target.value }))} /></div>
                  <div><Label>Divide By</Label><Input value={mappingForm.transform_divide_by} onChange={(e) => setMappingForm((state) => ({ ...state, transform_divide_by: e.target.value }))} /></div>
                  <div><Label>Offset</Label><Input value={mappingForm.transform_offset} onChange={(e) => setMappingForm((state) => ({ ...state, transform_offset: e.target.value }))} /></div>
                  <div><Label>Round</Label><Input value={mappingForm.transform_round} onChange={(e) => setMappingForm((state) => ({ ...state, transform_round: e.target.value }))} /></div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveMapping} disabled={busyAction === 'save-mapping'}>{mappingForm.id ? 'Update Mapping' : 'Create Mapping'}</Button>
                  <Button variant="outline" onClick={resetMappingForm}>Clear</Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Mappings</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {mappings.length === 0 ? <p className="text-slate-500">No mappings found.</p> : mappings.map((item) => (
                  <div key={item.id} className="border rounded-lg p-3 flex flex-col lg:flex-row lg:items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold">{item.mapping_name}</p>
                      <p className="text-sm text-slate-500">{item.canonical_key} • {item.mapping_type} • {item.connection_id}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => editMapping(item)}><Pencil className="w-4 h-4 mr-1" />Edit</Button>
                      <Button size="sm" variant="destructive" onClick={() => deleteMapping(item)} disabled={busyAction === `delete-mapping-${item.id}`}><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="links" className="space-y-4">
            <Card>
              <CardHeader><CardTitle>{linkForm.id ? 'Edit Asset Link' : 'Create Asset Link'}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Connection</Label>
                    <Select value={linkForm.connection_id} onValueChange={(value) => setLinkForm((state) => ({ ...state, connection_id: value }))}>
                      <SelectTrigger><SelectValue placeholder="Select connection" /></SelectTrigger>
                      <SelectContent>{orgScopedConnections.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Asset</Label>
                    <Select value={linkForm.asset_id} onValueChange={(value) => setLinkForm((state) => ({ ...state, asset_id: value }))}>
                      <SelectTrigger><SelectValue placeholder="Select asset" /></SelectTrigger>
                      <SelectContent>{orgScopedAssets.map((item) => <SelectItem key={item.id} value={item.id}>{item.name} ({item.asset_id})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>External Plant ID</Label>
                    <Input value={linkForm.external_plant_id} onChange={(e) => setLinkForm((state) => ({ ...state, external_plant_id: e.target.value }))} />
                  </div>
                  <div>
                    <Label>External Plant Name</Label>
                    <Input value={linkForm.external_plant_name} onChange={(e) => setLinkForm((state) => ({ ...state, external_plant_name: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={linkForm.link_status} onValueChange={(value) => setLinkForm((state) => ({ ...state, link_status: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Metadata (JSON)</Label>
                  <Textarea rows={4} value={linkForm.metadata_json} onChange={(e) => setLinkForm((state) => ({ ...state, metadata_json: e.target.value }))} />
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveLink} disabled={busyAction === 'save-link'}>{linkForm.id ? 'Update Link' : 'Create Link'}</Button>
                  <Button variant="outline" onClick={resetLinkForm}>Clear</Button>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Asset Links</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {links.length === 0 ? <p className="text-slate-500">No links found.</p> : links.map((item) => (
                  <div key={item.id} className="border rounded-lg p-3 flex flex-col lg:flex-row lg:items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold">{item.external_plant_name || item.external_plant_id}</p>
                      <p className="text-sm text-slate-500">Asset {item.asset_id} • Connection {item.connection_id}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => editLink(item)}><Pencil className="w-4 h-4 mr-1" />Edit</Button>
                      <Button size="sm" variant="destructive" onClick={() => deleteLink(item)} disabled={busyAction === `delete-link-${item.id}`}><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="jobs">
            <Card>
              <CardHeader><CardTitle>Ingestion Jobs</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {jobs.length === 0 ? <p className="text-slate-500">No jobs found.</p> : jobs.map((item) => (
                  <div key={item.id} className="border rounded-lg p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline">{item.status}</Badge>
                      <Badge variant="outline">{item.job_type}</Badge>
                      <span className="text-sm text-slate-500">Connection {item.connection_id || '-'}</span>
                    </div>
                    <p className="text-sm text-slate-700 mt-2">Processed: {item.records_processed} • Failed: {item.records_failed}</p>
                    {item.message && <p className="text-xs text-slate-500 mt-1">{item.message}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quarantine">
            <Card>
              <CardHeader><CardTitle>Quarantine</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {quarantine.length === 0 ? <p className="text-slate-500">No quarantined records.</p> : quarantine.map((item) => (
                  <div key={item.id} className="border rounded-lg p-3 flex flex-col lg:flex-row lg:items-center justify-between gap-2">
                    <div>
                      <div className="flex gap-2 flex-wrap">
                        <Badge variant="outline">{item.status}</Badge>
                        <Badge variant="outline">{item.error_code}</Badge>
                        <span className="text-sm text-slate-500">Connection {item.connection_id}</span>
                      </div>
                      <p className="text-sm text-slate-700 mt-2">{item.error_message || 'No error message'}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => replayQuarantine(item)} disabled={busyAction === `replay-${item.id}` || item.status === 'replayed'}>
                        Replay
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
